// Round 3 (independent review of PR #100) — same-order recovery flow.
//
// After a Local Delivery order's Stripe payment was captured but its
// accepted Uber quote could not be dispatched (expired before dispatch,
// or Uber definitively rejected the request), `payments/engine.py`
// automatically refunds the customer and never marks the order "paid"
// or dispatches anything — see that module's own docstring. This
// component is the customer-facing recovery path for THAT SAME order:
// request a genuinely fresh delivery quote, show it, require an
// EXPLICIT accept (never auto-accept a changed/higher fee), then create
// a new authoritative Stripe session for that new quote generation.
//
// Every step below calls the SAME backend routes the ordinary checkout
// flow (`CheckoutModal.jsx`) already uses — `POST .../local-delivery/
// quote`, `POST .../local-delivery/quotes/{id}/accept`, `POST
// .../payments/session` — against the EXISTING `order_id`, never
// creating a new retail order. The backend (`quote_service.
// request_quote`, `retail/delivery/dispatch/service.py::
// finalize_local_delivery_draft`, `payments/engine.py::create_session`)
// is what actually guarantees no duplicate charge/order/quote/delivery
// and that the OLD refunded session can never become authoritative
// again (see `payments/engine.py::_handle_paid_superseded_session` /
// `_handle_paid_orphaned_session`) — this component only drives that
// already-proven sequence; it enforces nothing on its own beyond never
// silently retrying with a stale quote and never auto-accepting a fee
// the customer hasn't explicitly seen.
//
// Whether this component is even OFFERED is decided server-side by
// `routes/payments.py`'s `/status` route (`local_delivery_recovery.
// eligible`) — this component doesn't re-derive that decision; the
// parent (`RetailPaymentReturn.jsx`) only mounts it when the backend
// says recovery is currently permitted (refund reached its terminal
// `refunded` state for one of the two well-understood dispatch-side
// reasons, and the order isn't separately flagged for reconciliation).
// A page reload or back-navigation simply re-polls that same
// server-computed truth — this component holds no client-only state
// that could desync from it.

import React, { useState } from "react";
import axios from "axios";
import { Loader2, Truck } from "lucide-react";
import { formatMoney } from "../../../lib/retailFormat";
import { loadLinkSlug, loadOrderAccessToken } from "../../../lib/retailSessionKeys";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

export default function LocalDeliveryRecovery({ slug, orderId, orderCode }) {
  // "idle" -> "requesting" -> "quoted" -> "accepting" (-> redirect)
  //                                    \-> "error" (request/accept failed)
  const [phase, setPhase] = useState("idle");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");

  const requestFreshQuote = async () => {
    setPhase("requesting");
    setError("");
    try {
      const { data } = await axios.post(
        `${RETAIL_BASE}/${slug}/local-delivery/quote`,
        { order_id: orderId },
      );
      logCustomerAuditEvent(slug, { event: "local_delivery_recovery_quote_received", orderCode });
      setQuote(data);
      setPhase("quoted");
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const msg = err?.response?.data?.detail?.message
        || "We couldn't get a new delivery quote right now. Please try again.";
      logCustomerAuditEvent(slug, { event: "local_delivery_recovery_quote_failed", reasonCode: code || "unknown", orderCode });
      setError(msg);
      setPhase("error");
    }
  };

  // Explicit customer action only — this is never called automatically,
  // and never re-run against a quote the customer hasn't just seen on
  // screen (the fee/expiry shown while `phase === "quoted"` is exactly
  // what this call accepts).
  const acceptAndPay = async () => {
    if (!quote) return;
    setPhase("accepting");
    setError("");
    try {
      await axios.post(`${RETAIL_BASE}/${slug}/local-delivery/quotes/${quote.quote_id}/accept`);
      logCustomerAuditEvent(slug, { event: "local_delivery_recovery_quote_accepted", orderCode });
      const { data: sess } = await axios.post(
        `${RETAIL_BASE}/${slug}/payments/session`,
        {
          order_id: orderId,
          method: "card",
          origin_url: window.location.origin,
          local_delivery: { quote_id: quote.quote_id },
          link_slug: loadLinkSlug(slug) || undefined,
        },
        // Security correction (Aug 2026) — this route now requires the
        // per-order access credential. `orderCode` here is always the
        // real order_code (see this file's own header comment: recovery
        // runs against the EXISTING order, never a new one), matching
        // the key `RetailOrderStatus.jsx` already stored it under.
        { headers: { "X-Order-Access": loadOrderAccessToken(slug, orderCode) } },
      );
      if (sess?.checkout_url) {
        // Same signal `CheckoutModal.jsx::startCardPayment` sets — tells
        // this page's own unload guard (if any) this navigation is
        // intentional, not an accidental tab close mid-payment.
        try { window.__rudrasAllowUnload = true; } catch (_e) { /* noop */ }
        window.location.href = sess.checkout_url;
        return;
      }
      setError("We couldn't start a new payment. Please try again.");
      setQuote(null);
      setPhase("error");
    } catch (err) {
      const det = err?.response?.data?.detail;
      const code = (det && typeof det === "object" && det.code) || "unknown";
      const msg = (det && typeof det === "object" && det.message)
        || "This delivery quote is no longer valid. Please request a new one.";
      logCustomerAuditEvent(slug, { event: "local_delivery_recovery_accept_failed", reasonCode: code, orderCode });
      // Never silently retry with a quote the backend just rejected
      // (expired, tampered, or superseded) — the customer must
      // explicitly request a genuinely fresh one.
      setError(msg);
      setQuote(null);
      setPhase("error");
    }
  };

  const startOver = () => {
    setQuote(null);
    setError("");
    setPhase("idle");
  };

  if (phase === "idle") {
    return (
      <button
        type="button"
        onClick={requestFreshQuote}
        className="mt-3 w-full px-5 py-2.5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 active:scale-[0.98]"
        data-testid="retail-recovery-request-quote"
      >
        <Truck className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
        Get a new delivery quote
      </button>
    );
  }

  if (phase === "requesting") {
    return (
      <div className="mt-3 flex items-center justify-center gap-2 text-emerald-200/80 text-sm" data-testid="retail-recovery-requesting">
        <Loader2 className="w-4 h-4 animate-spin" /> Getting a fresh delivery quote…
      </div>
    );
  }

  if (phase === "quoted" && quote) {
    return (
      <div className="mt-3 text-left bg-black/20 border border-emerald-200/15 rounded-xl p-4" data-testid="retail-recovery-quote">
        <div className="text-stone-100 text-sm font-semibold">New delivery fee</div>
        <div className="mt-1 text-2xl font-extrabold text-emerald-200" data-testid="retail-recovery-quote-fee">
          {formatMoney(quote.fee_amount, quote.currency)}
        </div>
        {quote.expires_at && (
          <div className="mt-1 text-xs text-emerald-200/60" data-testid="retail-recovery-quote-expiry">
            This quote is valid until {new Date(quote.expires_at).toLocaleTimeString()}.
          </div>
        )}
        <p className="mt-2 text-xs text-stone-300">
          This may be different from your original delivery fee. Nothing is charged until you
          accept and complete payment.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={startOver}
            className="h-10 rounded-lg border border-emerald-200/30 text-emerald-100 text-sm hover:bg-black/30"
            data-testid="retail-recovery-quote-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={acceptAndPay}
            className="h-10 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
            data-testid="retail-recovery-quote-accept"
          >
            Accept &amp; Pay
          </button>
        </div>
      </div>
    );
  }

  if (phase === "accepting") {
    return (
      <div className="mt-3 flex items-center justify-center gap-2 text-emerald-200/80 text-sm" data-testid="retail-recovery-accepting">
        <Loader2 className="w-4 h-4 animate-spin" /> Starting your new payment…
      </div>
    );
  }

  // phase === "error"
  return (
    <div className="mt-3" data-testid="retail-recovery-error">
      <p className="text-xs text-red-300">{error}</p>
      <button
        type="button"
        onClick={requestFreshQuote}
        className="mt-2 w-full px-5 py-2.5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 active:scale-[0.98]"
        data-testid="retail-recovery-retry"
      >
        Try again
      </button>
    </div>
  );
}
